import { MouseEvent, useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import Button from '../Button/Button'
import { CheckboxInput, PhoneInput } from '../Form'
import Header from '../Header/Header'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'
import Modal from '../Modal/Modal'

export const SMS_SIGNUP_MODAL_ID = 'sms-signup-modal'

const SMS_SIGNUP_TEXT_SUCCESS = 'You’re signed up for SMS text notifications!'

export interface SmsSignupModalProps {
  /**
   * Callback function when the form is submitted.
   */
  onSubmit: (data: Record<string, any>) => void
  isUpdatingSmsOptin?: boolean
  smsOptinSuccess?: boolean
  smsOptinError?: string
}

export default function SmsSignupModal({
  onSubmit,
  isUpdatingSmsOptin,
  smsOptinSuccess,
  smsOptinError,
}: SmsSignupModalProps) {
  const { control, handleSubmit } = useForm()
  const [isFormSubmitted, setFormSubmit] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const stopPropagation = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
  }, [])

  useEffect(() => {
    // keep our own internal state so we can reset the form if needed
    if (smsOptinError) {
      setFormError(smsOptinError)
      setFormSubmit(false)
    }
  }, [smsOptinError])

  useEffect(() => {
    // reset form state when modal is opened
    setFormError(null)
    setFormSubmit(false)
  }, [])

  const onFormSubmit = useCallback(
    (formData: Record<string, any>) => {
      onSubmit?.(formData)
      setFormSubmit(true)
    },
    [onSubmit],
  )

  return (
    <Modal
      id={SMS_SIGNUP_MODAL_ID}
      className="!p-12"
      dialogPanelClass="!max-w-[343px] md:!max-w-[565px] w-full"
      afterHide={() => {
        setFormSubmit(false)
        setFormError(null)
      }}
      content={
        <>
          {isFormSubmitted && !formError && (
            <div className="text-center max-w-[321px] mx-auto">
              <Icon
                icon="check-circle-filled"
                className="w-6 h-6 text-theme-accessibility mb-2"
              />
              <Header as="h2" className="type-body-1">
                {isUpdatingSmsOptin && (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                )}
                {!isUpdatingSmsOptin &&
                  smsOptinSuccess &&
                  SMS_SIGNUP_TEXT_SUCCESS}
              </Header>
            </div>
          )}

          {formError && (
            <div className="text-center max-w-[321px] mx-auto">
              <Icon
                icon="error-circle-filled"
                className="w-6 h-6 text-red-500 mb-2 mx-auto"
              />
              <Header as="h2" className="type-body-1 text-red-600">
                {formError}
              </Header>
            </div>
          )}

          {!isFormSubmitted && !formError && (
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <Header as="h2" className="type-body-1 text-center">
                Sign up for SMS
                <br className="inline md:hidden" /> text notifications
                <br />
                for your orders.
              </Header>
              <div className="mt-6">
                <PhoneInput
                  control={control}
                  name="phone"
                  label="Phone number*"
                  placeholder="555-555-5555"
                  rules={{
                    required: {
                      value: true,
                      message: 'Phone number is required.',
                    },
                  }}
                />
                <CheckboxInput
                  control={control}
                  name="terms"
                  label={
                    <>
                      I agree to the SMS Program{' '}
                      <Link
                        onClick={stopPropagation}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:no-underline font-semibold underline-offset-1 !inline"
                        href="#terms-of-use"
                        inheritFont
                        inline
                      >
                        Terms of Use
                      </Link>{' '}
                      and{' '}
                      <Link
                        onClick={stopPropagation}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:no-underline font-semibold underline-offset-1 !inline"
                        href="#privacy-policy"
                        inheritFont
                        inline
                      >
                        Privacy Policy
                      </Link>{' '}
                      and certify this is my phone number.
                    </>
                  }
                  className="[&]:items-start mt-2 md:mt-4"
                  rules={{
                    required: {
                      value: true,
                      message: 'Please confirm that you agree to the terms.',
                    },
                  }}
                />
              </div>
              <div className="mt-6 flex justify-center">
                <Button type="submit" className="w-full md:max-w-[185px]">
                  Submit
                </Button>
              </div>
              <div className="mt-6">
                <p className="type-legal-1 text-neutral-600">
                  Message and data rates may apply. Message frequency depends on{' '}
                  your activity. To opt out at any time, text STOP to ####. For{' '}
                  support, call us at{' '}
                  <span className="text-nowrap">xxx-xxx-xxxx</span>.
                </p>
              </div>
            </form>
          )}
        </>
      }
    />
  )
}
